import { AlertTriangle, Info, TriangleAlert } from 'lucide-react';
import type { InterfaceVerdict } from '@/hooks/useDeviceTrafficSeries';
import { StatTile } from '@/components/ui/stat-tile';
import { formatBits, formatCount, formatPercent, formatTotal } from './format';

/**
 * The headline: three or four numbers and one sentence saying what they mean.
 *
 * The sentence is the deliverable, not decoration. A traffic chart does not
 * tell a reader what is broken — errors, discards and link flaps do, and
 * traffic tells them whether the broken thing is also saturated. Both the
 * numbers and the sentence come from one server-side evaluation of one rule
 * table over the same series the charts below draw, so a tile can never
 * disagree with the headline above it.
 *
 * No sparkline in any tile. A twelve-point sparkline of zeros is decoration,
 * and the shapes live in the charts below.
 */
export function VerdictStrip({ verdict }: { verdict: InterfaceVerdict }) {
  const errorPeak = worst(verdict.peakErrorsIn, verdict.peakErrorsOut);
  const discardPeak = worst(verdict.peakDiscardsIn, verdict.peakDiscardsOut);
  // A count where the fetch could afford the endpoint reads, a peak rate
  // otherwise. The headline is the number an operator would put in a ticket;
  // the peak moves to the detail line rather than being dropped, because "412
  // frames" and "412 frames in one burst" are different tickets.
  const errorTotal = sum(verdict.totalErrorsIn, verdict.totalErrorsOut);
  const discardTotal = sum(verdict.totalDiscardsIn, verdict.totalDiscardsOut);
  // The peak in EITHER direction, matching the two tiles above and the
  // `peakUtilization` printed in this tile's own detail line — the server
  // folds that across both directions (`InterfaceSeries::peak_utilization`).
  // Reading `peakBitsIn` alone made the tile contradict itself on any port
  // that peaks outbound: "95.2 Mb/s" over "11% of line rate" on a 10 Gbps
  // link, where 95.2 Mb/s is 0.95%. `worst` also covers the port that
  // reports one direction and not the other, where the headline used to
  // print an em dash above a detail line quoting a real rate.
  const throughputPeak = worst(verdict.peakBitsIn, verdict.peakBitsOut);

  return (
    <div>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Errors"
          figures="proportional"
          value={errorTotal !== null ? formatTotal(errorTotal) : errorPeak === null ? '—' : formatCount(errorPeak)}
          detail={
            errorTotal !== null
              ? `${pair(verdict.totalErrorsIn, verdict.totalErrorsOut, formatTotal)} · peak ${formatCount(errorPeak ?? 0)}`
              : pair(verdict.peakErrorsIn, verdict.peakErrorsOut, formatCount)
          }
          tone={(errorTotal ?? errorPeak ?? 0) > 0 ? 'critical' : 'neutral'}
          toneLabel="errors counted"
        />
        <StatTile
          label="Discards"
          figures="proportional"
          value={discardTotal !== null ? formatTotal(discardTotal) : discardPeak === null ? '—' : formatCount(discardPeak)}
          detail={
            discardTotal !== null
              ? `${pair(verdict.totalDiscardsIn, verdict.totalDiscardsOut, formatTotal)} · peak ${formatCount(discardPeak ?? 0)}`
              : pair(verdict.peakDiscardsIn, verdict.peakDiscardsOut, formatCount)
          }
          tone={(discardTotal ?? discardPeak ?? 0) > 0 ? 'warning' : 'neutral'}
          toneLabel="frames dropped"
        />
        <StatTile
          label="Peak throughput"
          figures="proportional"
          value={throughputPeak === null ? '—' : formatBits(throughputPeak)}
          detail={
            verdict.peakUtilization !== null
              ? `${pair(verdict.peakBitsIn, verdict.peakBitsOut, formatBits)} · ${formatPercent(verdict.peakUtilization)} of line rate`
              : pair(verdict.peakBitsIn, verdict.peakBitsOut, formatBits)
          }
        />
        <StatTile
          label="Link"
          figures="proportional"
          value={verdict.operStatus ?? 'unreported'}
          detail={
            verdict.flaps === 0
              ? 'no state change in this window'
              : `${String(verdict.flaps)} state ${verdict.flaps === 1 ? 'change' : 'changes'} in this window`
          }
          tone={verdict.flaps > 0 ? 'warning' : 'neutral'}
          toneLabel="link changed state"
        />
      </div>

      <p className="mt-3 flex items-start gap-2 text-sm text-foreground">
        <VerdictIcon severity={verdict.severity} />
        <span>{verdict.sentence}</span>
      </p>
    </div>
  );
}

/**
 * Status rides an icon plus a label plus a `--sev-*` dot, never bare colour —
 * a reader with a colour vision deficiency gets the same finding as everyone
 * else.
 */
function VerdictIcon({ severity }: { severity: InterfaceVerdict['severity'] }) {
  switch (severity) {
    case 'critical':
      return (
        <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-sev-1" aria-label="critical" />
      );
    case 'warning':
      return <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-sev-3" aria-label="warning" />;
    default:
      return <Info className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" aria-label="note" />;
  }
}

/** Both directions together, or `null` when neither reported a count. */
function sum(a: number | null, b: number | null): number | null {
  if (a === null && b === null) return null;
  return (a ?? 0) + (b ?? 0);
}

function worst(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

/**
 * "in 3/s · out 0/s". A quantity the device never reported reads `not
 * reported`, which is not the same fact as a reported zero and must not print
 * as one.
 */
function pair(inbound: number | null, outbound: number | null, formatter: (v: number) => string): string {
  const left = inbound === null ? 'not reported' : formatter(inbound);
  const right = outbound === null ? 'not reported' : formatter(outbound);
  return `in ${left} · out ${right}`;
}
